import _createForOfIteratorHelper from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createForOfIteratorHelper";
import _classCallCheck from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
import _inherits from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/inherits";
import _createSuper from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createSuper";
/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
import { Command } from 'ckeditor5/src/core.js';
import TableUtils from '../tableutils.js';
import { updateNumericAttribute } from '../utils/common.js';
import { removeEmptyRowsColumns } from '../utils/structure.js';
/**
 * The merge cells command.
 *
 * The command is registered by {@link module:table/tableediting~TableEditing} as the `'mergeTableCells'` editor command.
 *
 * For example, to merge selected table cells:
 *
 * ```ts
 * editor.execute( 'mergeTableCells' );
 * ```
 */
var MergeCellsCommand = /*#__PURE__*/function (_Command) {
  _inherits(MergeCellsCommand, _Command);
  var _super = _createSuper(MergeCellsCommand);
  function MergeCellsCommand() {
    _classCallCheck(this, MergeCellsCommand);
    return _super.apply(this, arguments);
  }
  _createClass(MergeCellsCommand, [{
    key: "refresh",
    value:
    /**
     * @inheritDoc
     */
    function refresh() {
      var tableUtils = this.editor.plugins.get(TableUtils);
      var selectedTableCells = tableUtils.getSelectedTableCells(this.editor.model.document.selection);
      this.isEnabled = tableUtils.isSelectionRectangular(selectedTableCells);
    }
    /**
     * Executes the command.
     *
     * @fires execute
     */
  }, {
    key: "execute",
    value: function execute() {
      var model = this.editor.model;
      var tableUtils = this.editor.plugins.get(TableUtils);
      model.change(function (writer) {
        var selectedTableCells = tableUtils.getSelectedTableCells(model.document.selection);
        // All cells will be merged into the first one.
        var firstTableCell = selectedTableCells.shift();
        // Update target cell dimensions.
        var _getMergeDimensions = getMergeDimensions(firstTableCell, selectedTableCells, tableUtils),
          mergeWidth = _getMergeDimensions.mergeWidth,
          mergeHeight = _getMergeDimensions.mergeHeight;
        updateNumericAttribute('colspan', mergeWidth, firstTableCell, writer);
        updateNumericAttribute('rowspan', mergeHeight, firstTableCell, writer);
        var _iterator = _createForOfIteratorHelper(selectedTableCells),
          _step;
        try {
          for (_iterator.s(); !(_step = _iterator.n()).done;) {
            var tableCell = _step.value;
            mergeTableCells(tableCell, firstTableCell, writer);
          }
        } catch (err) {
          _iterator.e(err);
        } finally {
          _iterator.f();
        }
        var table = firstTableCell.findAncestor('table');
        // Remove rows and columns that become empty (have no anchored cells).
        removeEmptyRowsColumns(table, tableUtils);
        writer.setSelection(firstTableCell, 'in');
      });
    }
  }]);
  return MergeCellsCommand;
}(Command);
/**
 *  Merges two table cells. It will ensure that after merging cells with empty paragraphs the resulting table cell will only have one
 * paragraph. If one of the merged table cells is empty, the merged table cell will have contents of the non-empty table cell.
 * If both are empty, the merged table cell will have only one empty paragraph.
 */
export { MergeCellsCommand as default };
function mergeTableCells(cellBeingMerged, targetCell, writer) {
  if (!isEmpty(cellBeingMerged)) {
    if (isEmpty(targetCell)) {
      writer.remove(writer.createRangeIn(targetCell));
    }
    writer.move(writer.createRangeIn(cellBeingMerged), writer.createPositionAt(targetCell, 'end'));
  }
  // Remove merged table cell.
  writer.remove(cellBeingMerged);
}
/**
 * Checks if the passed table cell contains an empty paragraph.
 */
function isEmpty(tableCell) {
  var firstTableChild = tableCell.getChild(0);
  return tableCell.childCount == 1 && firstTableChild.is('element', 'paragraph') && firstTableChild.isEmpty;
}
function getMergeDimensions(firstTableCell, selectedTableCells, tableUtils) {
  var maxWidthOffset = 0;
  var maxHeightOffset = 0;
  var _iterator2 = _createForOfIteratorHelper(selectedTableCells),
    _step2;
  try {
    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var tableCell = _step2.value;
      var _tableUtils$getCellLo2 = tableUtils.getCellLocation(tableCell),
        row = _tableUtils$getCellLo2.row,
        column = _tableUtils$getCellLo2.column;
      maxWidthOffset = getMaxOffset(tableCell, column, maxWidthOffset, 'colspan');
      maxHeightOffset = getMaxOffset(tableCell, row, maxHeightOffset, 'rowspan');
    }
    // Update table cell span attribute and merge set selection on a merged contents.
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }
  var _tableUtils$getCellLo = tableUtils.getCellLocation(firstTableCell),
    firstCellRow = _tableUtils$getCellLo.row,
    firstCellColumn = _tableUtils$getCellLo.column;
  var mergeWidth = maxWidthOffset - firstCellColumn;
  var mergeHeight = maxHeightOffset - firstCellRow;
  return {
    mergeWidth: mergeWidth,
    mergeHeight: mergeHeight
  };
}
function getMaxOffset(tableCell, start, currentMaxOffset, which) {
  var dimensionValue = parseInt(tableCell.getAttribute(which) || '1');
  return Math.max(currentMaxOffset, start + dimensionValue);
}